import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons";
import WaybillDetails from "../components/WaybillDetails";
import TrackingFeed from "../components/TrackingFeed";
import DeliveryDetails from "../components/DeliveryDetails";
import CookieBanner from '../components/CookieBanner';
import ErrorMessage from "../components/ErrorMessage";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { brand } from '../config/brand';

function Tracking() {
  const { t } = useTranslation();
  const inputData = useParams().inputData; // Get the waybill code from the URL
  const [waybillCode, setWaybillCode] = useState(inputData || "");
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = useCallback(async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (!waybillCode) {
      setError(t("errors-waybill-empty-input"))
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${brand.trackingApiBaseUrl}/api/public/v1/tracking/${waybillCode}`);
      const data = await response.json();
      if (data.error === "Waybill does not exist") {
        setError(t("errors-waybill-not-found"));
        setTrackingData(null);
      } else {
        setTrackingData(data);
        setError(null);
      }
    } catch (error) {
      console.log(error);
      setError(t("errors-api-error"));
    }
    setIsLoading(false);
  }, [waybillCode, t]);

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(null);
      }, 5000); // 5 seconds
    }
    if (inputData) {
      handleSearch(null, inputData);
    }
  }, [error, inputData, handleSearch]);

  const renderTrackingDetails = () => {
    if (!trackingData || !trackingData.destination || !trackingData.destination.destination_tracks || trackingData.destination.destination_tracks.length === 0) {
      console.log(t("errors-waybill-tracks-not-found"));
      return null;
    }
    return (
      <div>
        <WaybillDetails info={trackingData.destination} />
        <DeliveryDetails deliveries={trackingData.destination.deliveries} />
        <TrackingFeed tracks={trackingData.destination.destination_tracks} />
      </div>
    );
  };

  return (
    <div className="mx-auto min-h-screen max-w-screen px-4 pt-32 pb-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl text-center animate-fade-up">
        <h1 className="font-display text-4xl font-extrabold uppercase italic tracking-tight text-ink-100 sm:text-5xl">
          {t('header-tracking')}
        </h1>
        <p className="mt-4 text-base text-ink-300">{t('tracking-waybill-input')}</p>
      </div>

      <div className="mx-auto mt-10 max-w-xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
        <form onSubmit={handleSearch}>
          <div className="flex items-stretch gap-2 rounded-2xl border border-white/10 bg-night-700/70 p-2 shadow-panel focus-within:border-signal-500/50">
            <input
              type="text"
              className="w-full border-0 bg-transparent px-4 py-3 text-base text-ink-100 placeholder:text-ink-400 focus:outline-none focus:ring-0"
              placeholder={t("tracking-waybill-input")}
              value={waybillCode}
              onFocus={(event) => event.target.select()}
              onChange={(e) => setWaybillCode(e.target.value)}
              required
            />
            <button
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-signal-500 px-6 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-signal-400 disabled:opacity-70"
              onClick={handleSearch}
              disabled={isLoading}
            >
              {isLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
            </button>
          </div>
        </form>

        {trackingData ? (
          <div className="mt-6">
            {renderTrackingDetails()}
          </div>
        ) : (
          <div>
            {error !== null && trackingData === null && (
              <ErrorMessage message={error} />
            )}
          </div>
        )}
      </div>
      <CookieBanner />
    </div>
  );
}

export default Tracking;
